import _ from 'lodash';
import React from 'react';
import { removeTypename } from 'shared/helpers/utils';
import { getAcceptedPaymentOptions, getPaymentFeeForMethod } from 'shared/helpers/dispensaries';
import { orderTypesToDisplayNames, paymentMethodsToDisplayNames } from 'shared/constants';
import { MobileEcommLinxInfoLink } from 'src/mobile-ecomm/components/linx-info-link';
import useCart from 'shared/hooks/use-cart';
import useUI from 'shared/hooks/use-ui';

// helpers
export const getFeeLabel = (paymentType, paymentFees, customFeeText = '') => {
  const feeInfo = getPaymentFeeForMethod(paymentType, paymentFees) || {};
  const { feeType, fee } = feeInfo;
  return fee
    ? ('sublabel', feeType === 'cents' ? `($${(fee / 100).toFixed(2)} ${customFeeText || 'fee'})` : `(${fee}% fee)`)
    : '';
};

const getValidOrderTypesList = (orderTypesConfigV2, paymentTypeKey) => {
  const validOrderTypes = [];
  _.forEach(removeTypename(orderTypesConfigV2), (orderTypeConfig, orderTypeKey) => {
    const orderTypeIsEnabled = orderTypeConfig.enableASAPOrdering || orderTypeConfig.enableScheduledOrdering;
    if (orderTypeIsEnabled && orderTypeKey !== 'kiosk' && orderTypeConfig.paymentTypes[paymentTypeKey]) {
      // Stripping out 'Pickup' string below since the V2 order types are longer/more numerous
      validOrderTypes.push(orderTypesToDisplayNames[orderTypeKey].replace(' Pickup', ''));
    }
  });
  return validOrderTypes;
};

const getPaymentMethodLabel = (paymentTypeKey) => paymentMethodsToDisplayNames[paymentTypeKey];

const getPaymentMethodValue = (paymentTypeKey) => {
  // handles conversion of credit/debit types to appropriate value, if applicable
  if (paymentTypeKey === 'creditCardAtDoor') {
    return 'creditCard';
  }
  if (paymentTypeKey === 'debit') {
    return 'debitCard';
  }

  return paymentTypeKey;
};

export const hasPbbCompletionPath = ({ dispensary, isKioskOrder }) =>
  Boolean(isKioskOrder || dispensary?.payByBankPreauthorizationAvailable);

export default function useDispensaryPaymentMethods(dispensaryProp) {
  const Cart = useCart();
  const UI = useUI();
  let dispensary;

  if ((UI?.isEmbedded || UI?.isStoreFront) && dispensaryProp) {
    dispensary = dispensaryProp?.dispensary;
  } else {
    dispensary = Cart?.order?.dispensary;
  }

  if (!dispensary) {
    return null;
  }

  const { paymentFees } = dispensary;
  const pbbCompletionPathAvailable = hasPbbCompletionPath({
    dispensary,
    isKioskOrder: Cart?.isKioskOrder,
  });

  const v2OrderType = !Cart?.orderType || Cart?.orderType === 'pickup' ? 'inStorePickup' : Cart?.orderType;
  const orderTypesConfig = _.omit(dispensary.orderTypesConfigV2, ['offerAnyPickupService', 'offerDeliveryService']);

  const paymentTypeKeys = [
    'dutchiePay',
    'rethinkPay',
    'cash',
    'check',
    'creditCardAtDoor',
    'creditCardByPhone',
    'debit',
    'alt36',
    'linx',
    'canPay',
    'payOnlineHypur',
    'paytender',
    'aeropay',
    'payOnlineMerrco',
    'payOnlineMoneris',
    'payOnlineChase',
  ];

  const paymentMethodsToBeDisplayed = _.filter(paymentTypeKeys, (paymentTypeKey) => {
    if (paymentTypeKey === 'dutchiePay' && !pbbCompletionPathAvailable) {
      return false;
    }

    // determine list of payment types that should be shown at checkout based on current order type.
    // For regular checkout, ALL dispo payment types for pickups/delivery should be visible, and
    // enabled/disabled as appropriate. For kiosk, only kiosk payment types should show.

    if (Cart?.isKioskOrder) {
      return dispensary.orderTypesConfig.kiosk.paymentTypes[paymentTypeKey];
    }
    // it's regular checkout; match against payment type list with kiosk-only types excluded.
    // We don't want to show a payment type at regular checkout if it's not usable for any pickup/delivery option.
    return getAcceptedPaymentOptions(dispensary)[paymentTypeKey];
  });

  const decoratedPaymentMethods = _.map(paymentMethodsToBeDisplayed, (paymentTypeKey) => {
    // add fee labelling
    const feeLabel = getFeeLabel(paymentTypeKey, paymentFees);
    let sublabel = feeLabel;

    // check if payment type is valid for currently-selected order type
    const orderType = v2OrderType;

    const validPaymentTypes = orderTypesConfig[Cart?.isKioskOrder ? 'kiosk' : orderType].paymentTypes;
    const disabledForSelectedOrderType = !validPaymentTypes[paymentTypeKey];

    // if payment method is not valid for currently-selected order type, add explanatory sublabel
    // telling user which order types it IS valid for
    if (disabledForSelectedOrderType) {
      const validOrderTypes = getValidOrderTypesList(orderTypesConfig, paymentTypeKey);
      sublabel = `(${validOrderTypes.join(', ').replace(/,(?!.*,)/gim, ' & ')} Only)`;
    }

    const option = {
      label: getPaymentMethodLabel(paymentTypeKey),
      sublabel,
      value: getPaymentMethodValue(paymentTypeKey),
      disabled: disabledForSelectedOrderType,
    };

    if (paymentTypeKey === 'rethinkPay') {
      option.sublabel = getFeeLabel(paymentTypeKey, paymentFees, 'Convenience Fee');
    }

    if (paymentTypeKey === 'linx') {
      option.label = (
        // eslint-disable-next-line react/jsx-filename-extension
        <span key='linx'>
          Linx{' '}
          {UI?.isMobileEcommApp ? (
            <MobileEcommLinxInfoLink />
          ) : (
            <a href='https://linx.us/' target='_blank' rel='noopener noreferrer' key='linx-a'>
              ?
            </a>
          )}
        </span>
      );
    }
    return option;
  });
  return decoratedPaymentMethods;
}
